import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { FaPaperPlane } from "react-icons/fa";
import Nav from "./Nav";
import Footer from "./Footer";
import "./home.css";
import useFetch from "../Components/UseFetch";
import StandardPageExpress from "../Components/StandardCardExpress";
import SuccessMessage from "../Components/SuccessPopup";
import ErrorMessage from "../Components/ErrorPopup";
import ConfirmationInputDialog from "../Components/ConfirmWithInput";
import { base64backendtoken } from "../HelperFx/datalinks.js";
import verifyToken from "../HelperFx/verifyJwt";

const REACT_EXPRESS = process.env.REACT_APP_EXPRESS_SERVER;

const emptySmtp = {
  Host: "",
  Port: "",
  UName: "",
  Password: "",
  Secure: false,
  Tls: "",
};

const authHeaders = {
  headers: {
    "Content-Type": "application/json",
    Authorization: `Basic ${base64backendtoken}`,
  },
};

const pickSmtp = (payload) => {
  if (!payload) return null;
  if (payload.smtp && !Array.isArray(payload.smtp)) return payload.smtp;
  if (Array.isArray(payload.smtp)) return payload.smtp[0] || null;
  if (payload.Host || payload.UName || payload.id) return payload;
  return null;
};

const mapSmtp = (row = {}) => ({
  Host: row.Host || "",
  Port: String(row.Port ?? ""),
  UName: row.UName || "",
  Password: row.Password || "",
  Secure: Boolean(row.Secure),
  Tls: row.Tls || "",
});

const SMTPCard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [fdata, setData] = useState(emptySmtp);
  const [recordId, setRecordId] = useState(location.state?.id || null);
  const [DocNo, setDocNo] = useState("New Email Account");
  const [confirmConfig, setConfirmConfig] = useState({});
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isSuccessOpen, setSuccessOpen] = useState(false);
  const [isErrorOpen, setErrorOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const jwttoken = sessionStorage.getItem("authToken");
  const decoded = verifyToken(jwttoken) || {};

  const { data: smtpPayload } = useFetch(
    recordId ? `${REACT_EXPRESS}/smtp/${recordId}` : null,
    base64backendtoken,
  );

  useEffect(() => {
    setRecordId(location.state?.id || null);
    if (!location.state?.id) {
      setData(emptySmtp);
      setDocNo("New Email Account");
    }
  }, [location.state?.id]);

  useEffect(() => {
    const row = pickSmtp(smtpPayload);
    if (!row) return;
    setData(mapSmtp(row));
    setRecordId(row.id);
    setDocNo(`${row.UName || row.Host || "SMTP"} - Email Account`);
  }, [smtpPayload]);

  const showError = (err) => {
    const backendMessage =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      "An error occurred while sending the request.";
    setPopupMessage(backendMessage);
    setErrorOpen(true);
  };

  const applySaved = (smtp) => {
    if (!smtp) return;
    setData(mapSmtp(smtp));
    setRecordId(smtp.id);
    setDocNo(`${smtp.UName || smtp.Host || "SMTP"} - Email Account`);
    if (smtp.id && location.state?.id !== smtp.id) {
      navigate("/SMTPCard", { replace: true, state: { id: smtp.id } });
    }
  };

  const handlePageBlur = async (field, value) => {
    if (field === "Password" && /^\*+$/.test(String(value || ""))) return;
    if (value === fdata[field]) return;

    const nextValues = { ...fdata, [field]: value };

    if (!recordId) {
      setData(nextValues);
      if (
        !nextValues.Host ||
        !nextValues.Port ||
        !nextValues.UName ||
        !nextValues.Password ||
        /^\*+$/.test(String(nextValues.Password || ""))
      ) {
        return;
      }
      try {
        const response = await axios.post(
          `${REACT_EXPRESS}/smtp`,
          {
            ...nextValues,
            ModifiedBy: decoded.userName,
          },
          authHeaders,
        );
        applySaved(response.data.smtp);
      } catch (err) {
        showError(err);
      }
      return;
    }

    try {
      const response = await axios.patch(
        `${REACT_EXPRESS}/smtp/${recordId}`,
        { [field]: value },
        authHeaders,
      );
      applySaved(response.data.smtp);
    } catch (err) {
      showError(err);
    }
  };

  const handleSendTestMail = async (inputValue) => {
    const recipient = String(inputValue || "").trim();
    if (!recipient) {
      setPopupMessage("Enter a recipient email.");
      setErrorOpen(true);
      return;
    }
    try {
      const response = await axios.post(
        `${REACT_EXPRESS}/testsmtp`,
        {
          recipient,
          UName: fdata.UName,
          id: recordId,
        },
        authHeaders,
      );
      setPopupMessage(response.data.message || "Test email sent successfully.");
      setSuccessOpen(true);
    } catch (err) {
      showError(err);
    }
  };

  const fields = [
    { name: "Host", label: "Host", type: "text" },
    { name: "Port", label: "Port", type: "text" },
    { name: "UName", label: "User Name", type: "text" },
    { name: "Password", label: "Password", type: "password" },
    { name: "Secure", label: "Secure", type: "checkbox" },
    { name: "Tls", label: "TLS", type: "text" },
  ];

  const actions = [
    {
      name: "Test Smtp",
      icon: <FaPaperPlane />,
      visible: true,
      fx: () => {
        setConfirmConfig({
          caption: "Send Test Mail",
          inputtype: "email",
          message: "Enter the mail to test with",
          onConfirm: handleSendTestMail,
          validate: (value) => {
            const email = String(value || "").trim();
            if (!email) return "Enter the mail to test with";
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
              return "Enter a valid email address";
            }
            return null;
          },
        });
        setConfirmOpen(true);
      },
    },
  ];

  return (
    <div className="Home">
      <Nav />
      <div className="dash-main settings-full" style={{ padding: "20px" }}>
        <StandardPageExpress
          title={DocNo}
          fields={fields}
          initialData={fdata}
          actions={actions}
          redirectTo="SMTPSetup"
          onBlur={handlePageBlur}
        />
      </div>
      <Footer />
      <ConfirmationInputDialog
        isOpen={isConfirmOpen}
        toggle={() => setConfirmOpen(!isConfirmOpen)}
        caption={confirmConfig.caption}
        message={confirmConfig.message}
        inputtype={confirmConfig.inputtype}
        validate={confirmConfig.validate}
        onConfirm={(value) => {
          confirmConfig.onConfirm(value);
          setConfirmOpen(false);
        }}
      />
      <SuccessMessage
        isOpen={isSuccessOpen}
        toggle={() => setSuccessOpen(!isSuccessOpen)}
        caption="Success"
        message={popupMessage}
      />
      <ErrorMessage
        isOpen={isErrorOpen}
        toggle={() => setErrorOpen(!isErrorOpen)}
        caption="Error"
        message={popupMessage}
      />
    </div>
  );
};

export default SMTPCard;
